<template>
  <div class="ib-dashboard">
    <!-- Header -->
    <div class="dash-header">
      <div class="dash-title">Performance Overview</div>
      <div class="dash-controls">
        <!-- Period selector -->
        <v-menu offset-y content-class="period-menu">
          <template v-slot:activator="{ on, attrs }">
            <div class="date-dropdown" v-bind="attrs" v-on="on">
              <div class="date-dropdown-inner">
                <div :class="period ? 'date-selected' : 'date-placeholder'">
                  {{ period ? periodLabel : 'Select a Date' }}
                </div>
                <v-icon size="16" color="#333">fa-regular fa-chevron-down</v-icon>
              </div>
            </div>
          </template>
          <v-list dense class="period-list">
            <v-list-item
              v-for="opt in periodOptions"
              :key="opt.value"
              @click="period = opt.value"
              :class="{ 'period-active': period === opt.value }"
            >
              <v-list-item-title>{{ opt.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Live / Demo toggle -->
        <div class="tab-buttons">
          <div
            class="tab-option"
            :class="{ active: accountType === 'live' }"
            @click="accountType = 'live'"
          >
            <v-icon size="16" :color="accountType === 'live' ? '#F8F8F8' : '#333'">fa-regular fa-list-ul</v-icon>
            <div class="tab-text">Live</div>
          </div>
          <div
            class="tab-option"
            :class="{ active: accountType === 'demo' }"
            @click="accountType = 'demo'"
          >
            <v-icon size="16" :color="accountType === 'demo' ? '#F8F8F8' : '#333'">fa-regular fa-chart-bar</v-icon>
            <div class="tab-text">Demo</div>
          </div>
        </div>
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="kpi-row">
      <div
        v-for="(kpi, index) in data.kpis"
        :key="'kpi-' + index"
        class="kpi-card"
      >
        <div class="kpi-inner">
          <div class="kpi-top">
            <div class="kpi-label">{{ kpi.label }}</div>
            <v-icon size="16" color="#333">{{ kpi.icon }}</v-icon>
          </div>
          <div class="kpi-value">{{ kpi.value }}</div>
          <div class="kpi-desc">{{ kpi.description }}</div>
        </div>
      </div>
    </div>

    <!-- Top 5 Cards -->
    <div class="top5-row">
      <!-- Top 5 Most Profitable Traders -->
      <div class="top5-card">
        <div class="top5-header">
          <div class="top5-title">Top 5 Most Profitable Traders</div>
          <div
            class="icon-btn"
            :class="{ active: chartView.profitable }"
            @click="chartView.profitable = !chartView.profitable"
          >
            <v-icon size="16" color="#333">fa-regular fa-chart-bar</v-icon>
          </div>
        </div>
        <div class="top5-divider"></div>

        <!-- Chart view -->
        <div v-if="chartView.profitable" class="bar-chart-area">
          <apexchart
            type="bar"
            height="250"
            :options="profitableChartOptions"
            :series="profitableChartSeries"
          />
        </div>

        <!-- List view -->
        <div v-else class="top5-list">
          <template v-for="(trader, i) in data.topProfitable">
            <div :key="'prof-' + i" class="top5-item">
              <div class="item-left">
                <div class="rank-badge">{{ i + 1 }}</div>
                <div class="item-name">{{ trader.name }}</div>
              </div>
              <div class="item-value">{{ trader.percentage }}%</div>
            </div>
            <div v-if="i < data.topProfitable.length - 1" :key="'prof-div-' + i" class="item-divider"></div>
          </template>
        </div>
      </div>

      <!-- Top 5 Most Active Traders -->
      <div class="top5-card">
        <div class="top5-header">
          <div class="top5-title">Top 5 Most Active Traders</div>
          <div
            class="icon-btn"
            :class="{ active: chartView.active }"
            @click="chartView.active = !chartView.active"
          >
            <v-icon size="16" color="#333">fa-regular fa-chart-pie</v-icon>
          </div>
        </div>
        <div class="top5-divider"></div>

        <!-- Chart view -->
        <div v-if="chartView.active" class="donut-chart-area">
          <apexchart
            type="donut"
            height="180"
            :options="activeChartOptions"
            :series="activeChartSeries"
          />
          <div class="donut-list">
            <div v-for="(trader, i) in data.topActive" :key="'act-dot-' + i" class="donut-list-item">
              <div class="donut-item-left">
                <div class="color-dot" :style="{ background: donutColors[i] }"></div>
                <div class="donut-item-name">{{ truncateName(trader.name) }}</div>
              </div>
              <div class="donut-item-value">{{ trader.trades }}</div>
            </div>
          </div>
        </div>

        <!-- List view -->
        <div v-else class="top5-list">
          <template v-for="(trader, i) in data.topActive">
            <div :key="'act-' + i" class="top5-item">
              <div class="item-left">
                <div class="rank-badge">{{ i + 1 }}</div>
                <div class="item-name">{{ trader.name }}</div>
              </div>
              <div class="item-value">{{ trader.trades }}</div>
            </div>
            <div v-if="i < data.topActive.length - 1" :key="'act-div-' + i" class="item-divider"></div>
          </template>
        </div>
      </div>

      <!-- Top 5 Most Traded Assets -->
      <div class="top5-card">
        <div class="top5-header">
          <div class="top5-title">Top 5 Most Traded Assets</div>
          <div
            class="icon-btn"
            :class="{ active: chartView.assets }"
            @click="chartView.assets = !chartView.assets"
          >
            <v-icon size="16" color="#333">fa-regular fa-chart-pie</v-icon>
          </div>
        </div>
        <div class="top5-divider"></div>

        <!-- Chart view -->
        <div v-if="chartView.assets" class="donut-chart-area">
          <apexchart
            type="donut"
            height="180"
            :options="assetsChartOptions"
            :series="assetsChartSeries"
          />
          <div class="donut-list">
            <div v-for="(asset, i) in data.topAssets" :key="'ast-dot-' + i" class="donut-list-item">
              <div class="donut-item-left">
                <div class="color-dot" :style="{ background: donutColors[i] }"></div>
                <div class="donut-item-name">{{ asset.name }}</div>
              </div>
              <div class="donut-item-value">{{ asset.trades }}</div>
            </div>
          </div>
        </div>

        <!-- List view -->
        <div v-else class="top5-list">
          <template v-for="(asset, i) in data.topAssets">
            <div :key="'ast-' + i" class="top5-item">
              <div class="item-left">
                <div class="rank-badge">{{ i + 1 }}</div>
                <div class="item-name">{{ asset.name }}</div>
              </div>
              <div class="item-value">{{ asset.trades }}</div>
            </div>
            <div v-if="i < data.topAssets.length - 1" :key="'ast-div-' + i" class="item-divider"></div>
          </template>
        </div>
      </div>
    </div>

    <!-- Insight Bar -->
    <div class="insight-wrapper">
      <div class="insight-bar">
        <div class="insight-inner">
          <div class="insight-label-group">
            <v-icon size="16" color="#333">fa-regular fa-sparkles</v-icon>
            <div class="insight-label">Insight |&nbsp;</div>
          </div>
          <div class="insight-text">{{ data.insight }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import DashboardService from '../services/dashboard.service'

export default {
  name: 'IBDashboard',
  components: {
    apexchart: VueApexCharts
  },

  data() {
    const partnerName = this.$store?.getters['ib/partnerName'] || 'Pepperstone'
    return {
      accountType: 'live',
      period: '1m',
      periodOptions: [
        { value: '1m', label: '1 Month Ago' },
        { value: '3m', label: '3 Months Ago' },
        { value: '6m', label: '6 Months Ago' },
        { value: '1y', label: '1 Year' }
      ],
      data: DashboardService.getDashboardData(partnerName, 'live', '1m'),
      chartView: {
        profitable: false,
        active: false,
        assets: false
      },
      donutColors: ['#2196F3', '#90CAF9', '#BBDEFB', '#E3F2FD', '#1565C0']
    }
  },

  watch: {
    accountType() { this.refreshData() },
    period() { this.refreshData() }
  },

  computed: {
    periodLabel() {
      const opt = this.periodOptions.find(o => o.value === this.period)
      return opt ? opt.label : ''
    },

    profitableChartOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Avenir, sans-serif' },
        plotOptions: {
          bar: { columnWidth: '60%', borderRadius: 5, distributed: true }
        },
        colors: ['#2196F3', '#64B5F6', '#90CAF9', '#BBDEFB', '#E3F2FD'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.data.topProfitable.map(t => this.truncateName(t.name)),
          labels: { style: { fontSize: '12px', fontFamily: 'Avenir, sans-serif', colors: '#3B3B3B' } }
        },
        yaxis: {
          max: 100,
          labels: {
            formatter: (val) => val + '%',
            style: { fontSize: '8px', colors: '#ABABAB' }
          }
        },
        legend: { show: false },
        grid: { borderColor: '#EBEBEB', strokeDashArray: 0 },
        tooltip: { y: { formatter: (val) => val + '%' } }
      }
    },

    profitableChartSeries() {
      return [{ name: 'Profit %', data: this.data.topProfitable.map(t => t.percentage) }]
    },

    activeChartOptions() {
      return {
        chart: { type: 'donut', fontFamily: 'Avenir, sans-serif' },
        colors: this.donutColors,
        labels: this.data.topActive.map(t => t.name),
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: '55%' } } },
        stroke: { width: 2, colors: ['#fff'] }
      }
    },

    activeChartSeries() {
      return this.data.topActive.map(t => t.trades)
    },

    assetsChartOptions() {
      return {
        chart: { type: 'donut', fontFamily: 'Avenir, sans-serif' },
        colors: this.donutColors,
        labels: this.data.topAssets.map(a => a.name),
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: '55%' } } },
        stroke: { width: 2, colors: ['#fff'] }
      }
    },

    assetsChartSeries() {
      return this.data.topAssets.map(a => a.trades)
    }
  },

  methods: {
    refreshData() {
      const partnerName = this.$store?.getters['ib/partnerName'] || 'Pepperstone'
      this.data = DashboardService.getDashboardData(partnerName, this.accountType, this.period)
    },

    truncateName(name) {
      if (!name) return ''
      const parts = name.split(' ')
      if (parts.length < 2) return name
      return parts[0] + ' ' + parts[1].substring(0, 3) + '...'
    }
  }
}
</script>

<style lang="scss" scoped>
.ib-dashboard {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

// Header
.dash-header {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.dash-title {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 700 !important;
  font-size: 30px !important;
  line-height: 40px;
  color: #3B3B3B !important;
}

.dash-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

// Date dropdown
.date-dropdown {
  border: 1px solid #ABABAB;
  border-radius: 5px;
  width: 265px;
  height: 35px;
  padding: 3px 12px;
}

.date-dropdown-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
}

.date-dropdown {
  cursor: pointer;

  &:hover {
    border-color: #333;
  }
}

.date-placeholder {
  flex: 1;
  font-family: 'Avenir', sans-serif;
  font-weight: 350;
  font-size: 16px;
  line-height: 1.5;
  color: #ABABAB;
}

.date-selected {
  flex: 1;
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;
  color: #14161B;
}

// Live/Demo toggle
.tab-buttons {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #EBEBEB;
  border-radius: 10px;
  height: 40px;
}

.tab-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 40px;
  padding: 0 15px;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &.active {
    background: #C03131;

    .tab-text {
      color: #F8F8F8 !important;
    }
  }
}

.tab-text {
  font-family: 'Avenir', sans-serif;
  font-weight: 350;
  font-size: 16px;
  line-height: 1.5;
  color: #3B3B3B;
}

// KPI Cards
.kpi-row {
  display: flex;
  gap: 20px;
  width: 100%;
}

.kpi-card {
  flex: 1;
  background: #F8F8F8;
  border: 1px solid #ABABAB;
  border-radius: 5px;
  padding: 10px;
}

.kpi-inner {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.kpi-label {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #14161B;
}

.kpi-value {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 700 !important;
  font-size: 30px !important;
  line-height: 40px;
  color: #14161B !important;
}

.kpi-desc {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #ABABAB;
}

// Top 5 Cards
.top5-row {
  display: flex;
  gap: 20px;
  width: 100%;
}

.top5-card {
  flex: 1;
  border: 1px solid #9A9A9A;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.top5-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.top5-title {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 1.5;
  color: #000 !important;
}

// Icon toggle button
.icon-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &.active {
    background: #28EA96;
  }

  &:hover:not(.active) {
    background: rgba(0, 0, 0, 0.04);
  }
}

.top5-divider {
  width: 100%;
  height: 1px;
  background: #EBEBEB;
}

// List view
.top5-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.top5-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rank-badge {
  width: 24px;
  background: #C3FFE3;
  border-radius: 5px;
  font-family: 'Avenir', sans-serif;
  font-weight: 350;
  font-size: 16px;
  line-height: 1.5;
  color: #005931;
  text-align: center;
}

.item-name {
  font-family: 'Avenir', sans-serif;
  font-weight: 350;
  font-size: 16px;
  line-height: 1.5;
  color: #14161B;
}

.item-value {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #ABABAB;
  text-align: right;
}

.item-divider {
  width: 100%;
  height: 1px;
  background: #EBEBEB;
}

// Chart views
.bar-chart-area {
  width: 100%;
}

.donut-chart-area {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.donut-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.donut-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.donut-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
}

.donut-item-name {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.28px;
  color: #3B3B3B;
}

.donut-item-value {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.28px;
  color: #ABABAB;
  text-align: right;
}

// Insight bar
.insight-wrapper {
  padding: 10px;
  border-radius: 10px;
}

.insight-bar {
  background: #EBEBEB;
  border-radius: 5px;
  padding: 10px;
}

.insight-inner {
  display: flex;
  align-items: center;
  gap: 5px;
}

.insight-label-group {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.insight-label {
  font-family: 'Avenir', sans-serif;
  font-weight: 800;
  font-size: 14px;
  line-height: 1.5;
  color: #3B3B3B;
  white-space: nowrap;
}

.insight-text {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #3B3B3B;
}

// Responsive
@media (max-width: 1100px) {
  .kpi-row {
    flex-wrap: wrap;
  }

  .kpi-card {
    flex: 1 1 calc(50% - 10px);
    min-width: 200px;
  }

  .top5-row {
    flex-direction: column;
  }
}

@media (max-width: 700px) {
  .kpi-card {
    flex: 1 1 100%;
  }

  .dash-controls {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .dash-title {
    font-size: 22px !important;
    line-height: 30px;
  }
}
</style>

<!-- Unscoped styles for v-menu dropdown (renders outside component) -->
<style lang="scss">
.period-menu {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;

  .period-list {
    padding: 4px 0 !important;

    .v-list-item {
      min-height: 40px !important;
      padding: 0 16px !important;
      cursor: pointer;

      .v-list-item__title {
        font-family: 'Avenir', sans-serif !important;
        font-size: 14px !important;
        color: #3B3B3B !important;
      }

      &:hover {
        background: #F5F5F5 !important;
      }

      &.period-active {
        background: rgba(192, 49, 49, 0.08) !important;

        .v-list-item__title {
          color: #C03131 !important;
          font-weight: 600 !important;
        }
      }
    }
  }
}
</style>
